import Swal from "sweetalert2";

export type UnsavedEntry = {
  getChanged: () => boolean;
  save: () => Promise<boolean>;
  title: string;
  parentKey?: string | null;
  discard?: () => void;
};

const entries = new Map<string, UnsavedEntry>();

/** Защита от рекурсии child→parent→child при сквозном сохранении */
const savingKeys = new Set<string>();

export const registerUnsaved = (panelKey: string, entry: UnsavedEntry) => {
  if (!panelKey) return;
  entries.set(panelKey, entry);
};

export const unregisterUnsaved = (panelKey: string) => {
  if (!panelKey) return;
  entries.delete(panelKey);
};

export const getUnsaved = (panelKey: string) => entries.get(panelKey);

export const getDirtyChildren = (parentKey: string): Array<UnsavedEntry & { key: string }> => {
  if (!parentKey) return [];
  const result: Array<UnsavedEntry & { key: string }> = [];
  for (const [key, entry] of entries) {
    if (entry.parentKey === parentKey && entry.getChanged()) {
      result.push({ ...entry, key });
    }
  }
  return result;
};

export const hasDirtyDescendants = (parentKey: string) =>
  getDirtyChildren(parentKey).length > 0;

export const isSavingUnsaved = (panelKey: string) =>
  !!(panelKey && savingKeys.has(panelKey));

/**
 * Выполнить save панели с защитой от повторного входа.
 * Если панель уже сохраняется в этом стеке — считаем успехом (реentranтность).
 */
export const runUnsavedSave = async (
  panelKey: string | null | undefined,
  save: () => Promise<boolean>
): Promise<boolean> => {
  if (!panelKey) return await save();
  if (savingKeys.has(panelKey)) return true;
  savingKeys.add(panelKey);
  try {
    return await save();
  } finally {
    savingKeys.delete(panelKey);
  }
};

/** Закрытие панели с учётом skip-флага (без повторного guard) */
let skipGuardKeys = new Set<string>();

export const withSkipUnsavedGuard = async (
  panelKey: string,
  fn: () => Promise<void> | void
) => {
  skipGuardKeys.add(panelKey);
  try {
    await fn();
  } finally {
    skipGuardKeys.delete(panelKey);
  }
};

export const shouldSkipUnsavedGuard = (panelKey: string) =>
  skipGuardKeys.has(panelKey);

/**
 * Один стандартный диалог при закрытии: есть изменения у панели и/или детей.
 * Сохранить → дети + сама форма; Не сохранять → discard; Отмена → остаёмся.
 */
export const confirmUnsavedForPanel = async (
  panelKey: string,
  options: { skipSelf?: boolean } = {}
): Promise<boolean> => {
  if (!panelKey || shouldSkipUnsavedGuard(panelKey)) return true;

  const children = getDirtyChildren(panelKey);
  const self = entries.get(panelKey);
  const selfDirty = !options.skipSelf && !!self?.getChanged();

  if (!children.length && !selfDirty) return true;

  const titles = [
    ...children.map((c) => c.title),
    ...(selfDirty && self?.title ? [self.title] : []),
  ].filter(Boolean);
  const uniqueTitles = [...new Set(titles)];
  const list =
    uniqueTitles.length > 1
      ? uniqueTitles.map((t) => `• ${t}`).join("<br>")
      : undefined;

  const result = await Swal.fire({
    title: "Данные изменены. Сохранить и закрыть?",
    html: list,
    text: list ? undefined : self?.title || undefined,
    confirmButtonText: "Сохранить",
    showDenyButton: true,
    denyButtonText: "Не сохранять",
    showCancelButton: true,
    cancelButtonText: "Отмена",
  });

  if (result.isDismissed) return false;

  if (result.isConfirmed) {
    for (const child of children) {
      if (savingKeys.has(child.key)) continue;
      const ok = await child.save();
      if (!ok) return false;
    }
    if (selfDirty && self) {
      return await self.save();
    }
    return true;
  }

  // Не сохранять
  for (const child of children) {
    child.discard?.();
  }
  if (selfDirty) self?.discard?.();
  return true;
};

/**
 * Перед сохранением родителя — тихо влить грязных детей (без Swal).
 */
export const confirmDirtyChildrenBeforeSave = async (
  parentKey: string
): Promise<boolean> => {
  if (!parentKey) return true;
  const children = getDirtyChildren(parentKey);
  if (!children.length) return true;

  for (const child of children) {
    if (savingKeys.has(child.key)) continue;
    const ok = await child.save();
    if (!ok) return false;
  }
  return true;
};
